import { FLAG_KEYS } from '@/const/flags';
import { InsuranceProduct } from '@/types/Subscription';

const VIP_SLUG = 'vip';

export function isVipInsuranceProduct(
  product: Pick<InsuranceProduct, 'slug' | 'name'> | undefined
): boolean {
  if (!product) return false;

  const slug = product.slug?.toLowerCase() ?? '';
  const name = product.name?.toLowerCase() ?? '';

  return (
    slug.includes(VIP_SLUG) || name === 'vip' || name.includes('vip insurance')
  );
}

/** Remove VIP from an insurance catalog (signup, upgrade, reactivate, etc.). */
export function filterVipFromInsuranceCatalog(
  products: InsuranceProduct[]
): InsuranceProduct[] {
  return products.filter((p) => !isVipInsuranceProduct(p));
}

function findInsuranceBySlugFragment(
  catalog: InsuranceProduct[],
  slugFragment: string
): InsuranceProduct | undefined {
  return catalog.find((product) =>
    product.slug.toLowerCase().includes(slugFragment)
  );
}

/** Match closet current insurance to an allowed option slug for form initial state. */
export function resolveInitialCoverageSlug(
  currentInsurance: InsuranceProduct | undefined,
  allowedInsurance: InsuranceProduct[]
): string {
  if (!allowedInsurance.length) return '';

  if (currentInsurance) {
    const match = allowedInsurance.find(
      (product) =>
        product.slug === currentInsurance.slug ||
        product.stripePlanId === currentInsurance.stripePlanId ||
        (isVipInsuranceProduct(product) &&
          isVipInsuranceProduct(currentInsurance))
    );

    if (match) return match.slug;
  }

  return allowedInsurance[0].slug;
}

/** Map a closet/Stripe insurance plan id to an EPCC catalog product. */
export function resolveInsuranceProductForClosetPlanId(
  closetInsurancePlanId: string | undefined,
  catalog: InsuranceProduct[]
): InsuranceProduct | undefined {
  if (!closetInsurancePlanId) return undefined;

  const directMatch = catalog.find(
    (product) => product.stripePlanId === closetInsurancePlanId
  );
  if (directMatch) return directMatch;

  const normalizedId = closetInsurancePlanId.toLowerCase();

  if (normalizedId.includes('vip')) {
    return catalog.find((product) => isVipInsuranceProduct(product));
  }

  if (normalizedId.includes('premium')) {
    return findInsuranceBySlugFragment(catalog, 'premium');
  }

  return findInsuranceBySlugFragment(catalog, 'standard');
}

/**
 * Réservé Manage Coverage page: Standard, Premium, and VIP from the EPCC catalog.
 * Closet stripe IDs often use plan aliases that do not match catalog
 * `insurance-stripe-id`, so options are resolved by slug and closet plan id heuristics.
 */
export function resolvePriveeManageCoverageOptions(
  catalog: InsuranceProduct[],
  currentInsuranceStripeId?: string
): InsuranceProduct[] {
  const options = new Map<string, InsuranceProduct>();

  const standard = findInsuranceBySlugFragment(catalog, 'standard');
  const premium = findInsuranceBySlugFragment(catalog, 'premium');
  const vip = catalog.find((product) => isVipInsuranceProduct(product));

  if (standard) options.set(standard.stripePlanId, standard);
  if (premium) options.set(premium.stripePlanId, premium);
  if (vip) options.set(vip.stripePlanId, vip);

  const currentProduct = resolveInsuranceProductForClosetPlanId(
    currentInsuranceStripeId,
    catalog
  );
  if (currentProduct) {
    options.set(currentProduct.stripePlanId, currentProduct);
  }

  return Array.from(options.values()).sort(
    (a, b) => a.price.asNumber - b.price.asNumber
  );
}

export function isVipInsuranceFeatureEnabled(
  flagValues: Record<string, boolean | string>
): boolean {
  return flagValues[FLAG_KEYS.PRIVEE_VIP_INSURANCE] === true;
}

/**
 * Strip VIP from insurance catalog for signup and manage-membership UIs.
 * VIP is only offered on the dedicated Réservé manage-coverage page when the flag is on.
 */
export function filterInsuranceForMembershipCatalog(
  products: InsuranceProduct[],
  options: { isVipInsuranceEnabled: boolean; isPriveeActive: boolean }
): InsuranceProduct[] {
  if (options.isVipInsuranceEnabled && options.isPriveeActive) {
    return products;
  }

  return filterVipFromInsuranceCatalog(products);
}

/** Manage Coverage page copy (From Figma). */
const MANAGE_COVERAGE_DESCRIPTION_BY_SLUG: Record<string, string> = {
  standard:
    'Covers normal wear or tear. Should an item be returned in severely damaged condition, you are responsible for paying a deductible based on the cost for Vivrelle to fix or replace the item.',
  premium:
    'Premium coverage applies to Premier, Classique, and Couture items and provides coverage for accidental damage beyond normal wear and tear.',
  vip: 'VIP coverage protects your Réservé items, as well as any Premier, Classique, or Couture items in your closet, and provides coverage for accidental damage beyond normal wear and tear.'
};

export function formatInsuranceMonthlyPrice(product: InsuranceProduct): string {
  if (product.price.asNumber === 0) {
    return '';
  }

  return product.price.asFormattedString.split('.')[0];
}

/** Radio / summary label, e.g. "Premium $18/month" or "Standard". */
export function formatInsuranceOptionLabel(product: InsuranceProduct): string {
  const price = formatInsuranceMonthlyPrice(product);
  if (!price) {
    return product.name;
  }

  return `${product.name} ${price}/month`;
}

/** Account card line, e.g. "VIP ($85/month)". */
export function formatInsuranceCoverageSummary(
  product: InsuranceProduct
): string {
  const price = formatInsuranceMonthlyPrice(product);
  if (!price) {
    return product.name;
  }

  return `${product.name} (${price}/month)`;
}

function resolveInsuranceDescriptionFromSlug(
  product: InsuranceProduct,
  copyBySlug: Record<string, string>
): string {
  const slugKey = product.slug.toLowerCase();

  if (isVipInsuranceProduct(product)) {
    return copyBySlug.vip;
  }

  if (slugKey.includes('standard')) {
    return copyBySlug.standard;
  }

  if (slugKey.includes('premium')) {
    return copyBySlug.premium;
  }

  return '';
}

/** Manage Coverage page — always use Figma copy, not EPCC catalog HTML. */
export function getManageCoverageDescription(
  product: InsuranceProduct
): string {
  return resolveInsuranceDescriptionFromSlug(
    product,
    MANAGE_COVERAGE_DESCRIPTION_BY_SLUG
  );
}
